import { supabase, supabaseEndpoint } from '../client';
import type { OrgRole } from './integrations';

// ---- types ---------------------------------------------------------------

export interface OrgMember {
  membership_id: string;
  user_id: string;
  store_id: string | null;
  role: OrgRole;
  email: string | null;
  display_name: string | null;
  is_self: boolean;
  created_at: string;
}

export interface OrgInvitation {
  id: string;
  email: string;
  role: OrgRole;
  store_id: string | null;
  /**
   * Stores this invitation is scoped to. `null`/empty means org-wide (the
   * invitee gets access to every store). Populated for operator/viewer
   * invitations that were scoped to specific locations.
   */
  store_ids: string[] | null;
  invited_by: string | null;
  invited_by_email: string | null;
  expires_at: string;
  created_at: string;
}

export interface InvitationToken {
  id: string;
  token: string;
  expires_at: string;
}

export type InvitationStatus = 'valid' | 'accepted' | 'revoked' | 'expired' | 'not_found';

export interface InvitationLookup {
  status: InvitationStatus;
  invitation_id: string | null;
  org_id: string | null;
  org_name: string | null;
  email: string | null;
  role: OrgRole | null;
  expires_at: string | null;
}

export interface AcceptedInvitation {
  org_id: string;
  store_id: string | null;
  role: OrgRole;
}

// ---- members + invitations (admin reads) --------------------------------

export async function listOrgMembers(orgId: string): Promise<OrgMember[]> {
  const { data, error } = await supabase()
    .schema('core')
    .rpc('rpc_org_list_members', { p_org_id: orgId });
  if (error) throw error;
  return (data ?? []) as unknown as OrgMember[];
}

/**
 * Deduplicated team-users list. One row per user with aggregated
 * effective role + org-wide-vs-per-store membership summary. Backs the
 * Settings -> Team "Members" table so users with both an org-wide row AND
 * per-store grants appear ONCE instead of N times. Org admins only.
 */
export interface OrgTeamUser {
  user_id: string;
  email: string | null;
  display_name: string | null;
  account_id: string | null;
  preferred_locale: string | null;
  effective_role: OrgRole;
  is_self: boolean;
  joined_at: string;
  last_sign_in_at: string | null;
  has_org_wide: boolean;
  store_count: number;
  org_wide_membership_id: string | null;
}

export async function listOrgTeamUsers(orgId: string): Promise<OrgTeamUser[]> {
  // RPC ships in 20260701020000_team_users_profile_rpcs.sql; cast through
  // any because generated.ts has not been regenerated yet.
  const sb = supabase() as unknown as {
    schema: (s: string) => { rpc: (n: string, args: unknown) => Promise<{ data: unknown; error: unknown }> };
  };
  const { data, error } = await sb
    .schema('core')
    .rpc('rpc_org_list_team_users', { p_org_id: orgId });
  if (error) throw error;
  return (data ?? []) as unknown as OrgTeamUser[];
}

/**
 * Header row for one user in an org -- email, display name, Crockford
 * account_id, verified phone, last sign-in, effective role. Backs the
 * "view user" drawer on Settings -> Team. Per-store grants are still
 * fetched via `listUserMemberships(orgId, userId)` in
 * `storeMemberships.ts`; this RPC is purely the profile header.
 */
export interface OrgTeamUserProfile {
  user_id: string;
  email: string | null;
  display_name: string | null;
  account_id: string | null;
  preferred_locale: string | null;
  email_confirmed_at: string | null;
  last_sign_in_at: string | null;
  created_at: string | null;
  phone: string | null;
  phone_verified_at: string | null;
  effective_role: OrgRole | null;
  joined_at: string | null;
  is_self: boolean;
  has_org_wide: boolean;
  org_wide_membership_id: string | null;
  store_count: number;
}

export async function getOrgTeamUserProfile(
  orgId: string,
  userId: string,
): Promise<OrgTeamUserProfile | null> {
  const sb = supabase() as unknown as {
    schema: (s: string) => { rpc: (n: string, args: unknown) => Promise<{ data: unknown; error: unknown }> };
  };
  const { data, error } = await sb
    .schema('core')
    .rpc('rpc_org_get_team_user_profile', { p_org_id: orgId, p_user_id: userId });
  if (error) throw error;
  const row = Array.isArray(data) ? data[0] : data;
  return (row ?? null) as OrgTeamUserProfile | null;
}

export async function listOrgInvitations(orgId: string): Promise<OrgInvitation[]> {
  const { data, error } = await supabase()
    .schema('core')
    .rpc('rpc_org_list_invitations', { p_org_id: orgId });
  if (error) throw error;
  return (data ?? []) as unknown as OrgInvitation[];
}

// ---- invite / revoke / resend -------------------------------------------

export interface InviteMemberInput {
  orgId: string;
  email: string;
  role: OrgRole;
  /** Legacy single-store scope. Prefer `storeIds`. */
  storeId?: string | null;
  /**
   * Stores to scope this invitation to. Omit / pass `null` or `[]` for an
   * org-wide invitation (access to all stores). Only `operator`/`viewer`
   * invitations may be scoped; owners/admins are always org-wide and the
   * RPC rejects a scoped owner/admin invite.
   */
  storeIds?: string[] | null;
}

/**
 * Creates (or refreshes the token on) an invitation. Returns the raw
 * token, which is what the operator app needs to build the accept URL and
 * email it via the `team_invite` template. The token is NEVER persisted
 * client-side; we hash it server-side before storing.
 */
export async function inviteMember(input: InviteMemberInput): Promise<InvitationToken> {
  // Cast to `any`: the 5-arg rpc_org_invite_member (p_store_ids) ships ahead
  // of the next generated.ts regen, same pattern as listOrgTeamUsers.
  const sb = supabase() as unknown as {
    schema: (s: string) => {
      rpc: (n: string, args: unknown) => Promise<{ data: unknown; error: unknown }>;
    };
  };
  const storeIds =
    input.storeIds && input.storeIds.length > 0 ? input.storeIds : undefined;
  const { data, error } = await sb.schema('core').rpc('rpc_org_invite_member', {
    p_org_id: input.orgId,
    p_email: input.email,
    p_role: input.role,
    p_store_id: input.storeId ?? undefined,
    p_store_ids: storeIds,
  });
  if (error) throw error;
  const row = Array.isArray(data) ? data[0] : data;
  if (!row) throw new Error('Invitation RPC returned no row.');
  return row as unknown as InvitationToken;
}

export async function revokeInvitation(invitationId: string): Promise<void> {
  const { error } = await supabase()
    .schema('core')
    .rpc('rpc_org_revoke_invitation', { p_invitation_id: invitationId });
  if (error) throw error;
}

export async function resendInvitation(invitationId: string): Promise<InvitationToken> {
  const { data, error } = await supabase()
    .schema('core')
    .rpc('rpc_org_resend_invitation', { p_invitation_id: invitationId });
  if (error) throw error;
  const row = Array.isArray(data) ? data[0] : data;
  if (!row) throw new Error('Resend RPC returned no row.');
  return row as unknown as InvitationToken;
}

// ---- role + remove ------------------------------------------------------

export async function updateMemberRole(
  orgId: string,
  userId: string,
  role: OrgRole,
): Promise<void> {
  const { error } = await supabase()
    .schema('core')
    .rpc('rpc_org_update_member_role', {
      p_org_id: orgId,
      p_user_id: userId,
      p_role: role,
    });
  if (error) throw error;
}

export async function removeOrgMember(orgId: string, userId: string): Promise<void> {
  const { error } = await supabase()
    .schema('core')
    .rpc('rpc_org_remove_member', {
      p_org_id: orgId,
      p_user_id: userId,
    });
  if (error) throw error;
}

// ---- public lookup + accept ---------------------------------------------

/**
 * Anon-callable: returns invitation metadata (org name, role, status)
 * so the /invite/:token landing page can render before the user signs in.
 * Always returns a row; status='not_found' for an unknown token.
 */
export async function lookupInvitation(token: string): Promise<InvitationLookup> {
  const { data, error } = await supabase()
    .schema('core')
    .rpc('rpc_lookup_invitation', { p_token: token });
  if (error) throw error;
  const row = Array.isArray(data) ? data[0] : data;
  return (row ?? {
    status: 'not_found',
    invitation_id: null,
    org_id: null,
    org_name: null,
    email: null,
    role: null,
    expires_at: null,
  }) as unknown as InvitationLookup;
}

/**
 * Authenticated. Verifies the signed-in user's email matches the invite,
 * creates the membership, and returns the org + default store to enter.
 */
export async function acceptInvitation(token: string): Promise<AcceptedInvitation> {
  const { data, error } = await supabase()
    .schema('core')
    .rpc('rpc_accept_invitation', { p_token: token });
  if (error) throw error;
  const row = Array.isArray(data) ? data[0] : data;
  if (!row) throw new Error('Accept RPC returned no row.');
  return row as unknown as AcceptedInvitation;
}

// ---- pending-invitation auto-accept -------------------------------------

export interface AcceptedPendingInvitation {
  org_id: string;
  store_id: string | null;
  role: OrgRole;
  org_name: string | null;
}

/**
 * Authenticated. Accepts every valid pending invitation pinned to the
 * signed-in user's email and returns the resulting (org, store, role,
 * org_name) rows. Returns `[]` when there are no pending invitations.
 *
 * Called by `<ActiveContextProvider>` on every session change so any
 * user who somehow signed in without going through `/invite/:token`
 * (e.g., older flows, a stale Supabase email-confirmation link, or
 * direct sign-in) still ends up in the org they were invited to,
 * instead of being bounced to the "create your first workspace" UI.
 *
 * Idempotent: subsequent calls return `[]` once everything has been
 * accepted.
 */
export async function autoAcceptPendingInvitations(): Promise<AcceptedPendingInvitation[]> {
  const { data, error } = await supabase()
    .schema('core')
    .rpc('rpc_auto_accept_pending_invitations');
  if (error) throw error;
  return (data ?? []) as unknown as AcceptedPendingInvitation[];
}

// ---- one-shot account creation for invitees ----------------------------

export type AcceptInviteErrorCode =
  | 'not_found'
  | 'revoked'
  | 'expired'
  | 'already_accepted'
  | 'account_exists'
  | 'unknown';

export interface AcceptInviteOk {
  ok: true;
  user_id: string;
  email: string;
  org_id: string;
  store_id: string | null;
  role: OrgRole;
}

export interface AcceptInviteErr {
  ok: false;
  code: AcceptInviteErrorCode;
  error: string;
}

export type AcceptInviteResult = AcceptInviteOk | AcceptInviteErr;

/**
 * Anonymous one-shot for invitees. POSTs `{ token, password }` to the
 * `accept-invite` Edge Function, which:
 *
 *   1. Validates the invitation token via `core.rpc_lookup_invitation`.
 *   2. Creates the auth.users row with `email_confirm: true` (or sets the
 *      password on an existing shell user) -- so the invitee skips
 *      Supabase's separate "confirm your email" step entirely. The token
 *      already proved they own the address.
 *   3. Marks the invitation accepted and creates the membership row via
 *      `core.rpc_admin_accept_invitation_for_user` (service-role only).
 *   4. Returns `{ user_id, email, org_id, store_id, role }`.
 *
 * After this resolves with `ok: true`, the caller should immediately call
 * `signInWithPassword(email, password)` to obtain a session and then
 * navigate to the workspace home.
 *
 * If the recipient already has a real account (with a password they set
 * themselves) we return `{ ok: false, code: 'account_exists' }` so the
 * caller can flip to sign-in mode -- we never silently overwrite a
 * password.
 *
 * Done via a plain `fetch` rather than `supabase.functions.invoke` for
 * the same reasons documented in `services/email.ts`: the gateway is
 * configured `verify_jwt = false`, the token IS the credential, and we
 * want a stable CORS contract.
 */
export async function acceptInviteAndCreateAccount(
  token: string,
  password: string,
): Promise<AcceptInviteResult> {
  const { url: baseUrl, anonKey } = supabaseEndpoint();
  const url = `${baseUrl.replace(/\/$/, '')}/functions/v1/accept-invite`;

  let resp: Response;
  try {
    resp = await fetch(url, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        // The Edge Functions gateway requires either an Authorization
        // header or an apikey header on every call, even when the
        // function itself is configured `verify_jwt = false`. Pass the
        // anon key so the gateway lets the request through; the function
        // ignores it and validates the invitation token instead.
        apikey: anonKey,
        Authorization: `Bearer ${anonKey}`,
      },
      body: JSON.stringify({ token, password }),
    });
  } catch (err) {
    const msg = err instanceof Error ? err.message : 'Network error.';
    return { ok: false, code: 'unknown', error: msg };
  }

  let payload: unknown = null;
  try {
    payload = await resp.json();
  } catch {
    payload = null;
  }

  if (resp.ok && payload && typeof payload === 'object' && (payload as any).ok === true) {
    const p = payload as AcceptInviteOk;
    return {
      ok: true,
      user_id: p.user_id,
      email: p.email,
      org_id: p.org_id,
      store_id: p.store_id ?? null,
      role: p.role,
    };
  }

  const errPayload = (payload ?? {}) as Partial<AcceptInviteErr>;
  return {
    ok: false,
    code: (errPayload.code as AcceptInviteErrorCode) ?? 'unknown',
    error:
      errPayload.error ??
      `Could not accept invitation (HTTP ${resp.status}). Try again or ask the inviter to resend.`,
  };
}

// ---- URL helper ---------------------------------------------------------

/**
 * Build the canonical /invite/:token URL for the operator app. Caller
 * passes the public app origin (env.appUrl) so we don't bake an
 * environment assumption into @pc/db.
 */
export function buildInviteUrl(appUrl: string, token: string): string {
  const base = appUrl.replace(/\/+$/, '');
  return `${base}/invite/${encodeURIComponent(token)}`;
}
